import { lazy, Suspense } from "react";

import { PartsMapErrorBoundary } from "./error-boundary";

import { ClientOnly } from "./client-only";

// Lazy load the D3-based inner component
const PartsForceGraphInner = lazy(() =>
    import("./parts-force-graph-inner-d3").then((module) => ({
        default: module.PartsForceGraphInnerD3,
    })),
);

interface PartForGraph {
    id: string;
    name: string;
    defaultImage?: { url?: string | null; signedUrl?: string } | null;
}

interface PartRelationship {
    id: string;
    type: string;
    description: string;
    sourcePartId: string;
    targetPartId: string;
    sourcePartInteractionId: string;
    targetPartInteractionId: string;
}

interface PartsForceGraphProps {
    parts: PartForGraph[];
    relationships: PartRelationship[];
    width?: number;
    height?: number;
    aspectRatio?: number; // height/width ratio, defaults to 9/16
    onNodeClick?: (partId: string) => void;
    // AIDEV-NOTE: Where this map renders ("session-notes", "part-detail", …). Tags render-health
    // telemetry and error reports so a spike can be traced to one surface.
    surface?: string;
}

const fallbackStyle = {
    minHeight: "200px",
    aspectRatio: `1 / ${9 / 16}`,
};
export function PartsForceGraph(props: PartsForceGraphProps) {
    return (
        <ClientOnly
            fallback={
                <div
                    className="border-brand-forest-200 bg-white relative w-full rounded-lg border"
                    style={fallbackStyle}
                ></div>
            }
        >
            {/* AIDEV-NOTE: The boundary must sit INSIDE ClientOnly — the imperative d3 build
                only runs on the client, so that's the only place it can throw. */}
            <PartsMapErrorBoundary
                surface={props.surface ?? "unknown"}
                nodeCount={props.parts.length}
                edgeCount={props.relationships.length}
            >
                <div className="border-brand-forest-200 bg-white relative w-full overflow-hidden rounded-lg border">
                    <Suspense
                        fallback={
                            <div
                                className="border-brand-forest-200 bg-white relative w-full rounded-lg border"
                                style={fallbackStyle}
                            ></div>
                        }
                    >
                        <PartsForceGraphInner {...props} />
                    </Suspense>
                </div>
            </PartsMapErrorBoundary>
        </ClientOnly>
    );
}
